/**
 * Friendly error message translation.
 *
 * The API surfaces concrete error codes for technical reasons (debugging,
 * smoke tests, log greping). The UI translates those to human-readable
 * messages a non-technical operator can understand + act on.
 *
 * If a code isn't mapped here we surface the raw code as a fallback (better
 * than silently hiding errors).
 */

interface Friendly {
  /** User-facing single sentence. */
  message: string
  /** Suggested next action. */
  hint?: string
}

const MAP: Record<string, Friendly> = {
  // GitHub connection
  GH_PAT_CHC_not_set: {
    message: 'GitHub connection isn’t set up yet.',
    hint: 'Ask your developer to add the GitHub access token in Worker settings (GH_PAT_CHC).',
  },
  GH_WEBHOOK_SECRET_CHC_not_set: {
    message: 'The webhook from your site repo isn’t configured.',
    hint: 'New AI-co-author drafts won’t appear in the cockpit until this is wired.',
  },
  // n8n factory
  N8N_REGEN_WEBHOOK_URL_not_set: {
    message: 'Section regeneration isn’t connected to the article factory yet.',
    hint: 'The button is ready — your developer just needs to wire n8n.',
  },
  N8N_FACTORY_WEBHOOK_URL_not_set: {
    message: 'The “new article” factory isn’t connected yet.',
    hint: 'Once n8n is wired, typing a keyword will trigger a fresh draft.',
  },
  factory_not_configured: {
    message: 'The article factory isn’t connected yet.',
    hint: 'Once n8n is wired, typing a keyword will trigger a fresh draft.',
  },
  // Preview
  CONTENTHUB_PREVIEW_SECRET_not_set: {
    message: 'The live preview pane isn’t configured.',
    hint: 'You can still edit + publish; the preview iframe just won’t render.',
  },
  preview_not_configured: {
    message: 'The live preview pane isn’t configured.',
    hint: 'You can still edit + publish; the preview iframe just won’t render.',
  },
  // Auth
  invalid_password: { message: 'Wrong password.', hint: 'Try again, or ask your developer if you’ve forgotten it.' },
  missing_token: { message: 'Please sign in.', hint: '' },
  invalid_or_expired_token: { message: 'Your session expired.', hint: 'Sign in again.' },
  // Publish / lint
  lint_blocking: {
    message: 'Some checks need fixing before you can publish.',
    hint: 'See the highlighted items below — click any to jump to the field.',
  },
  duplicate_publish: {
    message: 'You just published this exact article.',
    hint: 'Wait a moment, change something, or confirm to publish anyway.',
  },
  // Resources
  draft_not_found: { message: 'This article isn’t open as a draft yet.', hint: 'Click “Open as draft” on the article first.' },
  site_not_found: { message: 'This site isn’t configured in ContentHub.', hint: '' },
  article_not_found: { message: 'No such article in your repository.', hint: '' },
  // Cost
  daily_cap_would_exceed: {
    message: 'You’ve hit the daily cost cap.',
    hint: 'New generations resume tomorrow, or your developer can raise the cap.',
  },
}

/**
 * Translate any of:
 *   - an API error code string ('GH_PAT_CHC_not_set')
 *   - an ApiError-shaped object with body.error
 *   - a raw Error
 * Falls back to raw text if no mapping found.
 */
export function friendlyError(input: unknown): Friendly {
  const code = extractCode(input)
  if (code && MAP[code]) return MAP[code]
  // Fallback: show the raw text, no hint.
  if (typeof input === 'string') return { message: input }
  if (input && typeof input === 'object') {
    const msg = (input as { message?: string }).message
    if (msg) return { message: msg }
  }
  return { message: 'Something went wrong.' }
}

function extractCode(input: unknown): string | null {
  if (typeof input === 'string') return MAP[input] ? input : null
  if (!input || typeof input !== 'object') return null
  const obj = input as { body?: { error?: string }; status?: number }
  return obj.body?.error ?? null
}
